import { useMutation } from '@tanstack/react-query'
import { ViewRoom } from '@xeetr/types/models'
import { useContext } from 'react'

import { ActiveTenantContext } from '../context/common/Context'
import { invalidateRoomQueries } from '../lib/mutationHelpers'
import { RoomService } from '../services/Room.service'

import { useTenantRequiredService } from './common/useServiceFactory'

import { showErrorToastUnlessPermissionDenied, showToast } from '@/utils/toastMessage'

interface UseRoomsMutationReturn {
  createRoom: (room: ViewRoom) => void
  updateRoom: (params: { id: string; room: Partial<ViewRoom> }) => void
  deleteRoom: (id: string) => void
  isCreating: boolean
  isUpdating: boolean
  isDeleting: boolean
}

/**
 * Hook for managing room mutations (create, update, delete).
 *
 * Features:
 * - Granular PATCH updates for individual rooms
 * - Optimistic cache updates
 * - Automatic cache invalidation
 * - Toast notifications for success/error
 *
 * @example
 * ```tsx
 * const { updateRoom, isUpdating } = useRoomsMutation()
 *
 * updateRoom({
 *   id: 'room-123',
 *   room: { name: 'Updated Name' }
 * })
 * ```
 */
export const useRoomsMutation = (): UseRoomsMutationReturn => {
  const { tenantId } = useContext(ActiveTenantContext)
  const getService = useTenantRequiredService(RoomService)

  const createMutation = useMutation({
    mutationFn: async (room: ViewRoom) => {
      const service = await getService()
      return service.createRoom(room)
    },
    onSuccess: () => {
      invalidateRoomQueries(tenantId)
      showToast({ severity: 'success', detail: 'Raum erfolgreich erstellt.' })
    },
    onError: (error) => {
      console.error('Room create error:', error)
      showErrorToastUnlessPermissionDenied(error, {
        severity: 'error',
        detail: 'Fehler beim Erstellen des Raums.',
      })
    },
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, room }: { id: string; room: Partial<ViewRoom> }) => {
      const service = await getService()
      return service.patchRoom(id, room)
    },
    onSuccess: () => {
      invalidateRoomQueries(tenantId)
      showToast({ severity: 'success', detail: 'Raum erfolgreich aktualisiert.' })
    },
    onError: (error) => {
      console.error('Room update error:', error)
      showErrorToastUnlessPermissionDenied(error, {
        severity: 'error',
        detail: 'Fehler beim Aktualisieren des Raums.',
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const service = await getService()
      return service.deleteRoom(id)
    },
    onSuccess: () => {
      invalidateRoomQueries(tenantId)
      showToast({ severity: 'success', detail: 'Raum erfolgreich gelöscht.' })
    },
    onError: (error) => {
      console.error('Room delete error:', error)
      showErrorToastUnlessPermissionDenied(error, {
        severity: 'error',
        detail: 'Fehler beim Löschen des Raums.',
      })
    },
  })

  return {
    createRoom: createMutation.mutate,
    updateRoom: updateMutation.mutate,
    deleteRoom: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  }
}
